/**
 * Memory Editor - עריכת זיכרון לקוח
 * Shared controlled form for editing a user's AI memory. Used by both the
 * end-user Brain panel and the admin modal. Owns no API calls — the parent
 * passes the initial payload and handles onSave(patch).
 */

import { useState } from 'react'
import { X, Plus, Loader2, AlertCircle } from 'lucide-react'

export interface MemoryStructured {
  keyFacts: string[]
  preferences: string[]
  concerns: string[]
}

export interface MemoryPayload {
  enabled: boolean
  autoUpdateEnabled: boolean
  hasMemory: boolean
  summary: string
  topics: string[]
  structured: MemoryStructured
  lastSummarizedAt?: string | null
  lastEditedBy?: 'user' | 'admin' | null
  lastEditedAt?: string | null
}

export interface MemoryPatch {
  summary: string
  topics: string[]
  keyFacts: string[]
  preferences: string[]
  concerns: string[]
  autoUpdateEnabled: boolean
}

interface MemoryEditorProps {
  initial: MemoryPayload
  saving?: boolean
  error?: string | null
  onSave: (patch: MemoryPatch) => void
  onCancel: () => void
}

// Convert a newline-separated textarea value into a clean string array.
const linesToArray = (value: string): string[] =>
  value
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)

export default function MemoryEditor({ initial, saving, error, onSave, onCancel }: MemoryEditorProps) {
  const [summary, setSummary] = useState(initial.summary || '')
  const [topics, setTopics] = useState<string[]>(initial.topics || [])
  const [topicDraft, setTopicDraft] = useState('')
  const [keyFacts, setKeyFacts] = useState((initial.structured?.keyFacts || []).join('\n'))
  const [preferences, setPreferences] = useState((initial.structured?.preferences || []).join('\n'))
  const [concerns, setConcerns] = useState((initial.structured?.concerns || []).join('\n'))
  const [autoUpdate, setAutoUpdate] = useState(initial.autoUpdateEnabled !== false)

  const addTopic = () => {
    const t = topicDraft.trim()
    if (t && !topics.includes(t) && topics.length < 12) {
      setTopics([...topics, t])
    }
    setTopicDraft('')
  }

  const removeTopic = (t: string) => setTopics(topics.filter((x) => x !== t))

  const handleSave = () => {
    onSave({
      summary: summary.trim(),
      topics,
      keyFacts: linesToArray(keyFacts),
      preferences: linesToArray(preferences),
      concerns: linesToArray(concerns),
      autoUpdateEnabled: autoUpdate,
    })
  }

  return (
    <div className="space-y-4" dir="rtl">
      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700" role="alert">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>{error}</p>
        </div>
      )}

      {/* Summary */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-1">סיכום</label>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={4}
          maxLength={1400}
          className="w-full border border-gray-300 rounded-lg p-2 text-sm resize-y"
          placeholder="סיכום חופשי על הלקוח..."
        />
      </div>

      {/* Topics */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-1.5">נושאים</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {topics.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 text-xs rounded-full px-2.5 py-1">
              {t}
              <button onClick={() => removeTopic(t)} aria-label={`הסר ${t}`} className="hover:text-primary-900">
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={topicDraft}
            onChange={(e) => setTopicDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addTopic()
              }
            }}
            maxLength={60}
            className="flex-1 border border-gray-300 rounded-lg p-2 text-sm"
            placeholder="הוסף נושא ולחץ Enter"
          />
          <button onClick={addTopic} className="btn btn-outline text-sm px-3" aria-label="הוסף נושא">
            <Plus size={15} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Preferences — the main thing a user wants to enter */}
      <ListField label="העדפות" hint="שורה לכל העדפה" value={preferences} onChange={setPreferences} />
      <ListField label="עובדות" hint="שורה לכל פריט" value={keyFacts} onChange={setKeyFacts} />
      <ListField label="דגשים" hint="שורה לכל פריט" value={concerns} onChange={setConcerns} />

      {/* Auto-update freeze toggle */}
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={autoUpdate} onChange={(e) => setAutoUpdate(e.target.checked)} />
        המשך לעדכן את הזיכרון אוטומטית מהשיחות
      </label>
      {!autoUpdate && (
        <p className="text-xs text-gray-500 -mt-2">
          כשהאפשרות כבויה, יועצי לא ישנה את הזיכרון הזה אוטומטית — רק מה שהזנת יישמר.
        </p>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="btn btn-outline text-sm" disabled={saving}>
          ביטול
        </button>
        <button onClick={handleSave} className="btn btn-primary text-sm flex items-center gap-1.5" disabled={saving}>
          {saving && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
          שמור
        </button>
      </div>
    </div>
  )
}

function ListField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-1">
        {label} <span className="font-normal text-gray-400 text-xs">({hint})</span>
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full border border-gray-300 rounded-lg p-2 text-sm resize-y"
      />
    </div>
  )
}
